/**
 * Enums for Fishing Empire
 * All game-wide enumerated types
 */

/**
 * Quality/Rarity tiers for caught fish and items
 */
export enum Quality {
  COMMON = 'common',           // White - Basic quality
  UNCOMMON = 'uncommon',       // Green - Slightly better
  RARE = 'rare',               // Blue - Notable quality
  EPIC = 'epic',               // Purple - Exceptional
  LEGENDARY = 'legendary',     // Orange - Extremely rare
  UNIQUE = 'unique',           // Yellow - One-of-a-kind with special attributes
  QUEST = 'quest',             // Light Blue - Quest-specific items
}

/**
 * Fish rarity categories
 */
export enum FishRarity {
  COMMON = 'common',
  UNCOMMON = 'uncommon',
  RARE = 'rare',
  EPIC = 'epic', // Very rare (Arctic Grayling)
  LEGENDARY = 'legendary', // Not used in Phase 1
}

/**
 * Equipment slot types
 */
export enum EquipmentSlot {
  ROD = 'rod',
  REEL = 'reel',
  BAIT = 'bait',
  LINE = 'line',
  TACKLE = 'tackle',
  TRAP = 'trap',
  // Future slots
  BOAT = 'boat',
}

/**
 * Current fishing action state
 */
export enum FishingState {
  IDLE = 'idle',
  CASTING = 'casting',
  WAITING = 'waiting',
  REELING = 'reeling',
  COMPLETE = 'complete',
}

/**
 * Types of automation buddies
 */
export enum BuddyType {
  ROD_BUDDY = 'rod_buddy',
  RUNNER_BUDDY = 'runner_buddy',
  // Future buddies (Phase 2+)
  PROCESSOR_BUDDY = 'processor_buddy',
  MANAGER_BUDDY = 'manager_buddy',
}

/**
 * Quality value multipliers
 */
export const QualityMultipliers: Record<Quality, number> = {
  [Quality.COMMON]: 1.0,       // Base value
  [Quality.UNCOMMON]: 1.3,     // 30% bonus
  [Quality.RARE]: 1.7,         // 70% bonus
  [Quality.EPIC]: 2.5,         // 150% bonus
  [Quality.LEGENDARY]: 4.0,    // 300% bonus
  [Quality.UNIQUE]: 5.0,       // 400% bonus - extremely valuable
  [Quality.QUEST]: 0.5,        // Low value - not meant to be sold
};

/**
 * Base quality roll probabilities
 */
export const QualityChances: Record<Quality, number> = {
  [Quality.COMMON]: 0.50,      // 50% - Most common
  [Quality.UNCOMMON]: 0.30,    // 30% - Fairly common
  [Quality.RARE]: 0.15,        // 15% - Uncommon
  [Quality.EPIC]: 0.04,        // 4% - Rare
  [Quality.LEGENDARY]: 0.009,  // 0.9% - Very rare
  [Quality.UNIQUE]: 0.001,     // 0.1% - Extremely rare (1 in 1000)
  [Quality.QUEST]: 0.0,        // 0% - Never rolls naturally, only from quests
};

/**
 * Quality display colors for UI
 */
export const QualityColors: Record<Quality, string> = {
  [Quality.COMMON]: '#FFFFFF',      // White
  [Quality.UNCOMMON]: '#1EFF00',    // Green
  [Quality.RARE]: '#0070DD',        // Blue
  [Quality.EPIC]: '#A335EE',        // Purple
  [Quality.LEGENDARY]: '#FF8000',   // Orange
  [Quality.UNIQUE]: '#FFD700',      // Yellow/Gold
  [Quality.QUEST]: '#00CCFF',       // Light Blue
};

/**
 * Quality display names (capitalized for UI)
 */
export const QualityNames: Record<Quality, string> = {
  [Quality.COMMON]: 'Common',
  [Quality.UNCOMMON]: 'Uncommon',
  [Quality.RARE]: 'Rare',
  [Quality.EPIC]: 'Epic',
  [Quality.LEGENDARY]: 'Legendary',
  [Quality.UNIQUE]: 'Unique',
  [Quality.QUEST]: 'Quest Item',
};

/**
 * Quality ordering for comparison (higher = better)
 */
export const QUALITY_ORDER: Record<Quality, number> = {
  [Quality.COMMON]: 0,
  [Quality.UNCOMMON]: 1,
  [Quality.RARE]: 2,
  [Quality.EPIC]: 3,
  [Quality.LEGENDARY]: 4,
  [Quality.UNIQUE]: 5,
  [Quality.QUEST]: 6,
};

/** Returns true if the fish quality is below the threshold (should be auto-sold) */
export function isBelowQualityThreshold(fishQuality: Quality, threshold: Quality): boolean {
  return QUALITY_ORDER[fishQuality] < QUALITY_ORDER[threshold];
}

/**
 * Rarity spawn weight probabilities
 */
export const RarityWeights: Record<FishRarity, number> = {
  [FishRarity.COMMON]: 0.65,
  [FishRarity.UNCOMMON]: 0.25,
  [FishRarity.RARE]: 0.09,
  [FishRarity.EPIC]: 0.04, // Very rare (Arctic Grayling)
  [FishRarity.LEGENDARY]: 0.01, // Disabled in Phase 1
};
